import { useNavigate } from "react-router-dom";
import { Gift, Minus, Plus, Trash2, Truck } from "lucide-react";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { useCart } from "@/context/CartContext";
import { BUNDLE } from "@/lib/bundle";

const FREE_SHIPPING = 50;
const formatPrice = (p) => `€${p.toFixed(2).replace(".", ",")}`;

export const CartDrawer = () => {
  const { items, isOpen, setIsOpen, setQty, removeItem, subtotal, bundle } = useCart();
  const navigate = useNavigate();

  const progress = Math.min(subtotal / FREE_SHIPPING, 1);
  const remaining = FREE_SHIPPING - subtotal;

  return (
    <Sheet open={isOpen} onOpenChange={setIsOpen}>
      <SheetContent
        side="right"
        data-testid="cart-drawer"
        className="w-full sm:max-w-md bg-[#0D1320] border-l border-slate-800 text-white flex flex-col p-0"
      >
        <SheetHeader className="px-6 pt-6 pb-4 border-b border-slate-800">
          <SheetTitle className="font-display text-3xl tracking-wide text-white uppercase">
            Winkelmand
          </SheetTitle>
        </SheetHeader>

        <div className="flex-1 overflow-y-auto px-6 py-4">
          {items.length === 0 ? (
            <div className="h-full flex flex-col items-center justify-center text-center gap-3" data-testid="cart-empty-state">
              <p className="font-display text-3xl text-slate-500 uppercase tracking-wide">Nog helemaal leeg</p>
              <p className="text-sm text-slate-400">Tijd om dat te veranderen. Kies je blaster.</p>
              <button
                data-testid="cart-empty-shop-button"
                onClick={() => {
                  setIsOpen(false);
                  navigate("/#blasters");
                }}
                className="mt-2 bg-gradient-to-r from-[#00FF66] to-[#00E5FF] text-slate-950 font-extrabold text-sm uppercase px-6 py-3 rounded-full"
              >
                Bekijk blasters
              </button>
            </div>
          ) : (
            <ul className="space-y-4">
              {items.map((item) => (
                <li
                  key={item.id}
                  data-testid={`cart-item-${item.id}`}
                  className="flex gap-4 bg-slate-900/70 border border-slate-800 rounded-2xl p-3"
                >
                  <img src={item.image} alt={item.name} className="w-20 h-20 rounded-xl object-cover shrink-0" />
                  <div className="flex-1 min-w-0">
                    <p className="font-bold text-sm text-white truncate">{item.name}</p>
                    <p className="text-[#00E5FF] font-bold text-sm mt-0.5">{formatPrice(item.price)}</p>
                    <div className="mt-2 flex items-center gap-2">
                      <button
                        data-testid={`cart-qty-minus-${item.id}`}
                        onClick={() => setQty(item.id, item.qty - 1)}
                        className="w-7 h-7 rounded-full bg-slate-800 hover:bg-slate-700 flex items-center justify-center transition-colors"
                      >
                        <Minus className="w-3.5 h-3.5" />
                      </button>
                      <span className="w-6 text-center text-sm font-bold" data-testid={`cart-qty-${item.id}`}>
                        {item.qty}
                      </span>
                      <button
                        data-testid={`cart-qty-plus-${item.id}`}
                        onClick={() => setQty(item.id, item.qty + 1)}
                        className="w-7 h-7 rounded-full bg-slate-800 hover:bg-slate-700 flex items-center justify-center transition-colors"
                      >
                        <Plus className="w-3.5 h-3.5" />
                      </button>
                      <button
                        data-testid={`cart-remove-${item.id}`}
                        onClick={() => removeItem(item.id)}
                        className="ml-auto text-slate-500 hover:text-[#FF087B] transition-colors"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </div>

        {items.length > 0 && (
          <div className="px-6 py-5 border-t border-slate-800 space-y-4">
            <div>
              <div className="flex items-center gap-2 text-xs text-slate-400 mb-2">
                <Truck className="w-3.5 h-3.5 text-[#00FF66]" />
                {remaining > 0 ? (
                  <span data-testid="cart-shipping-progress-text">
                    Nog <b className="text-white">{formatPrice(remaining)}</b> tot gratis verzending
                  </span>
                ) : (
                  <span className="text-[#00FF66] font-bold" data-testid="cart-shipping-progress-text">
                    Gratis verzending unlocked!
                  </span>
                )}
              </div>
              <div className="h-1.5 bg-slate-800 rounded-full overflow-hidden">
                <div
                  data-testid="cart-shipping-progress-bar"
                  className="h-full bg-gradient-to-r from-[#00FF66] to-[#00E5FF] rounded-full transition-all duration-500"
                  style={{ width: `${progress * 100}%` }}
                />
              </div>
            </div>

            <div
              data-testid="cart-bundle-box"
              className={`rounded-2xl border px-4 py-3 ${bundle.active ? "border-[#00FF66]/40 bg-[#00FF66]/5" : "border-slate-800 bg-slate-900/60"}`}
            >
              <div className="flex items-center gap-2 text-xs">
                <Gift className={`w-3.5 h-3.5 ${bundle.active ? "text-[#00FF66]" : "text-[#FF087B]"}`} />
                {bundle.active ? (
                  <span className="text-[#00FF66] font-bold" data-testid="cart-bundle-active-text">
                    Bundelkorting actief: €{BUNDLE.discountPerBlaster} korting per blaster
                  </span>
                ) : (
                  <span className="text-slate-300" data-testid="cart-bundle-nudge-text">{bundle.nudge}</span>
                )}
              </div>
              {bundle.active && bundle.nudge && (
                <p className="text-[11px] text-slate-400 mt-1 pl-5" data-testid="cart-bundle-gift-nudge">{bundle.nudge}</p>
              )}
              {bundle.gift && (
                <p className="text-[11px] text-[#00E5FF] font-bold mt-1 pl-5" data-testid="cart-bundle-gift-unlocked">
                  Gratis {BUNDLE.giftName} toegevoegd aan je bestelling!
                </p>
              )}
            </div>

            <div className="space-y-1.5">
              {bundle.discount > 0 && (
                <div className="flex items-center justify-between text-sm text-[#00FF66] font-bold">
                  <span>Bundelkorting ({bundle.qty} blasters)</span>
                  <span data-testid="cart-bundle-discount">-{formatPrice(bundle.discount)}</span>
                </div>
              )}
              <div className="flex items-center justify-between">
                <span className="text-slate-400 text-sm">Subtotaal (excl. verzending)</span>
                <span className="text-xl font-extrabold text-white" data-testid="cart-subtotal">
                  {formatPrice(subtotal - bundle.discount)}
                </span>
              </div>
            </div>

            <button
              data-testid="cart-checkout-button"
              onClick={() => {
                setIsOpen(false);
                navigate("/checkout");
              }}
              className="w-full bg-gradient-to-r from-[#00FF66] to-[#00E5FF] text-slate-950 font-extrabold uppercase tracking-wider py-4 rounded-full glow-lime hover:glow-lime-strong hover:scale-[1.02] active:scale-95 transition-all duration-200"
            >
              Naar afrekenen
            </button>
          </div>
        )}
      </SheetContent>
    </Sheet>
  );
};
